import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import { formatDateRange } from '../utils/format'
import { eventService } from '../services/eventService'
import { attendeeAuth } from '../services/attendeeAuth'
import { resolveAssetUrl } from '../services/assetUrl'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'
import { LoadingState } from '../components/ui/States'
import type { EventListItem } from '../data/types'

/**
 * Public landing: a directory of published events. Selecting an event opens a
 * passwordless (magic-link) sign-in scoped to that event. If the auth backend
 * is unavailable (offline demo), selecting an event enters it directly.
 */
export default function LandingPage() {
  const navigate = useNavigate()
  const { authAvailable, isAuthenticated, me } = useAttendeeAuth()
  const [events, setEvents] = useState<EventListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<EventListItem | null>(null)

  // Already signed in → go straight to the attendee's own event when we know
  // its slug; otherwise fall back to /app (resolves the current event).
  useEffect(() => {
    if (isAuthenticated) {
      navigate(me?.eventSlug ? `/e/${me.eventSlug}` : '/app', { replace: true })
    }
  }, [isAuthenticated, me, navigate])

  useEffect(() => {
    eventService
      .listPublishedEvents()
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false))
  }, [])

  const pick = (ev: EventListItem) => {
    // Offline/demo (no auth backend): go straight into the event.
    if (!authAvailable) {
      navigate(`/e/${ev.slug}`)
      return
    }
    setSelected(ev)
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: 1,
          color: '#fff',
          background: 'linear-gradient(150deg, #0075BC 0%, #1C5580 55%, #14384f 100%)',
          p: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: -100,
            left: -80,
            width: 380,
            height: 380,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(91,198,204,0.35) 0%, rgba(91,198,204,0) 70%)',
          }}
        />
        <Box sx={{ position: 'relative', maxWidth: 460 }}>
          <Typography variant="overline" sx={{ opacity: 0.85, fontWeight: 700 }}>
            Vericast Events
          </Typography>
          <Typography variant="h3" fontWeight={700} sx={{ mt: 1, lineHeight: 1.1 }}>
            Your events, all in one place
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 400, opacity: 0.92 }}>
            Choose your event to view the agenda, speakers, resources, and more.
          </Typography>
        </Box>
      </Box>

      {/* Right panel: directory or sign-in */}
      <Box
        sx={{
          flex: { xs: 'unset', md: '0 0 460px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 4, md: 6 },
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="xs" disableGutters>
          <Box sx={{ mb: 4 }}>
            <Logo />
          </Box>

          {selected ? (
            <EventSignIn event={selected} onBack={() => setSelected(null)} />
          ) : (
            <>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Select your event
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Pick the event you're attending to continue.
              </Typography>

              {loading ? (
                <LoadingState />
              ) : events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No published events are available right now. Please check back
                  soon, or contact your Vericast event manager.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {events.map((ev) => (
                    <Card
                      key={ev.id}
                      variant="outlined"
                      onClick={() => pick(ev)}
                      sx={{
                        borderRadius: 3,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 1.5,
                        pr: 2,
                        overflow: 'hidden',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      {ev.heroImage ? (
                        <Box
                          sx={{
                            width: 92,
                            minHeight: 72,
                            alignSelf: 'stretch',
                            flexShrink: 0,
                            backgroundImage: `url(${resolveAssetUrl(ev.heroImage)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 92,
                            minHeight: 72,
                            alignSelf: 'stretch',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background:
                              'linear-gradient(135deg, rgba(0,117,188,0.12), rgba(91,198,204,0.12))',
                          }}
                        >
                          <EventRoundedIcon color="primary" />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0, py: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {ev.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatDateRange(ev.startsAt, ev.endsAt)}
                          {ev.location ? ` · ${ev.location}` : ''}
                        </Typography>
                      </Box>
                      <ArrowForwardRoundedIcon color="action" />
                    </Card>
                  ))}
                </Stack>
              )}

              <Button
                component={RouterLink}
                to="/admin"
                size="small"
                color="inherit"
                sx={{ color: 'text.secondary', mt: 3 }}
              >
                Vericast staff sign-in
              </Button>
            </>
          )}
        </Container>
      </Box>
    </Box>
  )
}

/** Per-event magic-link sign-in. */
function EventSignIn({ event, onBack }: { event: EventListItem; onBack: () => void }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleContinue = async () => {
    if (!email.trim()) return
    setSubmitting(true)
    try {
      await attendeeAuth.requestLink(email.trim(), event.slug)
      setSent(true)
    } catch {
      // Neutral "check your inbox" state regardless (no enumeration leak).
      setSent(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={onBack}
        size="small"
        sx={{ color: 'text.secondary', mb: 2 }}
      >
        All events
      </Button>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {event.name}
        </Typography>
        <Chip label="Sign in" size="small" />
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CalendarMonthRoundedIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>
            {formatDateRange(event.startsAt, event.endsAt)}
          </Typography>
        </Stack>
        {event.location && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PlaceRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>
              {event.location}
            </Typography>
          </Stack>
        )}
      </Stack>

      {sent ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: 'rgba(0,117,188,0.06)',
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography fontWeight={700} gutterBottom>
            Check your inbox
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If {email} is on the guest list for {event.name}, we've sent a secure
            sign-in link. Open it on this device to continue.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Enter the email on your invitation and we'll send you a secure
            sign-in link.
          </Typography>
          <TextField
            label="Email address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
            autoComplete="email"
          />
          <Button
            variant="contained"
            size="large"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={handleContinue}
            disabled={submitting || !email.trim()}
          >
            {submitting ? 'Sending…' : 'Email me a link'}
          </Button>
          <Divider sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Invited guests only
            </Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Having trouble? Contact your Vericast event manager.
          </Typography>
        </Stack>
      )}
    </>
  )
}
